import type { NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink } from "@apollo/client";
import { useMemo } from "react";

import { getUploadLink } from "@remix/data/getUploadLink/getUploadLink";
import { useInitialProps } from "@shared/context/InitialPropsProvider";
import { getErrorLink } from "@shared/data/client/links/errorLink/errorLink";
import { getAddAuthLink } from "@shared/data/client/links/getAddAuthLink/getAddAuthLink";
import { getAddEventIdLink } from "@shared/data/client/links/getAddEventIdLink/getAddEventIdLink";
import { getAddUTMLink } from "@shared/data/client/links/getAddUTMLink/getAddUTMLink";
import { getAuthorizedAndValidDataLink } from "@shared/data/client/links/getAuthorizedAndValidDataLink/getAuthorizedAndValidDataLink";
import { fingerprintLink } from "@shared/data/client/links/getFingerprintLink/getFingerprintLink";
import { getPersistedQueriesLink } from "@shared/data/client/links/getPersistedQueriesLink/getPersistedQueriesLink";
import { getQueryLoadingLink } from "@shared/data/client/links/getQueryLoadingLink/getQueryLoadingLink";
import { initializeEventIds } from "@shared/helpers/eventIdManager";
import { useCheckIsEmerson } from "@shared/hooks/useCheckIsEmerson/useCheckIsEmerson";
import { useLoadingData } from "@shared/hooks/useLoadingData/useLoadingData";
import { isProduction, isDevelopment } from "@shared/utils/fullscriptEnv/fullscriptEnv";
import { getMutationLoadingLink } from "@shared/utils/getMutationLoadingLink/getMutationLoadingLink";

import { getCache } from "./cache";

const useClient = (): ApolloClient<NormalizedCacheObject> => {
  const { roleId, userId } = useInitialProps();
  const isEmerson = useCheckIsEmerson();

  const { flippers } = useInitialProps();
  const { incrementMutationLoading, decrementMutationLoading } = useLoadingData();
  const gqlPersistedQueriesFlipper = flippers.includes("gql_persisted_queries");
  const iamTrustedDeviceEnabled = flippers.includes("iam_trusted_device");

  initializeEventIds();

  const getLink = (isWordpress: boolean) => {
    const mutationLoadingLink = getMutationLoadingLink({
      incrementMutationLoading,
      decrementMutationLoading,
    });

    let links = [getErrorLink(), mutationLoadingLink];

    if ((roleId || userId) && !isWordpress) {
      links = [
        ...links,
        getAuthorizedAndValidDataLink({ roleId }),
        getAddAuthLink({ userId, roleId }),
      ];
    }

    links.push(getAddUTMLink());
    links.push(getAddEventIdLink());

    if (gqlPersistedQueriesFlipper && !isWordpress) {
      const persistedQueriesLink = getPersistedQueriesLink();
      links.push(persistedQueriesLink);
    }

    if (isDevelopment()) {
      const queryLoadingLink = getQueryLoadingLink();
      links = [...links, queryLoadingLink];
    }

    if (iamTrustedDeviceEnabled) {
      links.push(fingerprintLink);
    }

    return ApolloLink.from([...links, getUploadLink({ isEmerson, isWordpress })]);
  };

  const directionalLink = ApolloLink.split(
    operation => operation.getContext().clientName === "wordpress",
    getLink(true),
    getLink(false)
  );

  const getClient = () => {
    return new ApolloClient({
      link: directionalLink,
      cache: getCache(),
      connectToDevTools: !isProduction(),
    });
  };

  return useMemo(() => {
    const client = getClient();
    // Disable network fetches during hydration
    // Prevents cache-and-network queries from causing server/client content mismatches
    // It's re-enabled in ApolloClientProvider after hydration
    client.disableNetworkFetches = true;

    return client;
  }, [gqlPersistedQueriesFlipper]);
};

export { useClient };
